// src/pages/VisionAssistLauncher.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { startVisionSession } from "../utils/visionAssistApi";
import { isIOSApp } from "../utils/platform";
import { FaCheck, FaTimes } from "react-icons/fa";
import { FiMic } from "react-icons/fi";

// 🔐 Firebase auth (same pattern as ContractInsightPage)
import { getAuth, onAuthStateChanged } from "firebase/auth";

const HOST = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const GOAL_MAX_LENGTH = 2000;
const MIN_RECORD_MS = 1000;
const MIN_BLOB_BYTES = 15000;

const MONTHLY_PLAN_TYPES = new Set([
  "monthly",
  "monthly_essential",
  "monthly_pro",
  "monthly_elite",
  "monthly_unlimited",
]);

function parseDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function activeByDateAndFlag(data = {}) {
  if (data.is_active === false || data.isActive === false) return false;

  const candidates = [
    data.current_period_end,
    data.currentPeriodEnd,
    data.subscription_current_period_end,
    data.subscriptionCurrentPeriodEnd,
    data.expires_at,
    data.expiresAt,
  ];

  for (const value of candidates) {
    if (!value) continue;
    const date = parseDate(value);
    if (!date) continue;
    return date.getTime() > Date.now();
  }

  return true;
}

function monthlyPlanIsActive(data = {}) {
  const planType = String(data.plan_type || data.planType || "")
    .trim()
    .toLowerCase();
  return MONTHLY_PLAN_TYPES.has(planType) && activeByDateAndFlag(data);
}

function trialPlanIsActive(data = {}) {
  const planType = String(data.plan_type || data.planType || "")
    .trim()
    .toLowerCase();
  return planType === "trial_7d" && activeByDateAndFlag(data);
}

function readEntitlementMinutes(bucket, ...keys) {
  if (!bucket || typeof bucket !== "object") {
    return { found: false, value: 0 };
  }

  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(bucket, key)) continue;
    const n = Number(bucket[key]);
    if (Number.isFinite(n)) {
      return { found: true, value: Math.max(0, n) };
    }
  }

  return { found: false, value: 0 };
}

export default function VisionAssistLauncher() {
  const navigate = useNavigate();

  // Screen detection (your existing breakpoint) + device classification (mobile/tablet vs desktop)
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [showDeviceWarning, setShowDeviceWarning] = useState(false);

  // Input focus (for strong focus ring)
  const [focusedField, setFocusedField] = useState(null); // "formType" | "siteHost" | "goal" | null

  // Form fields
  const [formType, setFormType] = useState("");
  const [siteHost, setSiteHost] = useState("");
  const [goal, setGoal] = useState("");
  const [goalLimitNotice, setGoalLimitNotice] = useState("");

  // Inline errors
  const [formTypeError, setFormTypeError] = useState(null);
  const [goalError, setGoalError] = useState(null);
  const [termsError, setTermsError] = useState(null);

  // ✅ Terms of Use / Privacy checkbox (must be checked before starting)
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Auth / token
  const [idToken, setIdToken] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authUser, setAuthUser] = useState(null);

  // Status + billing
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(null); // { type: "ok" | "error" | "billing", msg }
  const [sessionInfo, setSessionInfo] = useState(null);

  // Step 3 voice billing gate — same fail-closed pattern as Doc Writer.
  const [billingSnapshot, setBillingSnapshot] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingLoaded, setBillingLoaded] = useState(false);
  const [billingError, setBillingError] = useState("");
  const [voiceBillingDialogOpen, setVoiceBillingDialogOpen] = useState(false);

  // Step 3 microphone state — mirrors the proven Doc Writer voice flow.
  const [recording, setRecording] = useState(false);
  const [voiceReady, setVoiceReady] = useState(false);
  const [voiceText, setVoiceText] = useState("");
  const [waveformData, setWaveformData] = useState([]);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isAcceptingVoice, setIsAcceptingVoice] = useState(false);
  const [voiceNotice, setVoiceNotice] = useState(null);

  // iOS-native app detection (for billing language behavior)
  const onIOSApp = isIOSApp();

  // ✅ Desktop-only autofocus (Step 1)
  const formTypeRef = useRef(null);
  const goalRef = useRef(null);
  const termsRef = useRef(null);

  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const streamRef = useRef(null);
  const recordedBlobRef = useRef(null);
  const recordedMimeRef = useRef("");
  const pendingAcceptRef = useRef(false);
  const stopPromiseRef = useRef(null);
  const recordStartAtRef = useRef(0);
  const isFlatRef = useRef(false);
  const lastVoiceActionAtRef = useRef(0);
  const lastVoiceNoticeRef = useRef({ key: "", at: 0 });
  const goalSelectionRef = useRef({ start: null, end: null });
  const pendingGoalCaretRef = useRef(null);

  // --- Responsive behavior (same 1024px rule used across the app) --- //
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const apply = () => setIsSmallScreen(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  // --- Device classification: treat non-desktop as "mobile/tablet" --- //
  // We use multiple signals to avoid iPad edge-cases.
  const computeIsMobileLike = () => {
    try {
      const coarse = window.matchMedia?.("(pointer: coarse)")?.matches;
      const noHover = window.matchMedia?.("(hover: none)")?.matches;
      const small = window.matchMedia?.("(max-width: 1023px)")?.matches;

      if ((coarse || noHover) && small) return true;
      if (small && window.innerWidth <= 768) return true;

      return false;
    } catch {
      return window.innerWidth <= 768;
    }
  };

  // ✅ One-time warning gate per page load (re-shows on refresh)
  const MOBILE_WARNING_KEY = "va_mobile_warning_dismissed";

  useEffect(() => {
    const apply = () => {
      const mobileLike = computeIsMobileLike();
      setIsMobileDevice(mobileLike);

      let dismissed = false;
      try {
        dismissed = sessionStorage.getItem(MOBILE_WARNING_KEY) === "1";
      } catch {}

      if (mobileLike && !dismississedSafe(dismissed)) {
        setShowDeviceWarning(true);
      }
      // If dismissed, never re-open during this page visit (prevents keyboard resize popups).
    };

    apply();
    window.addEventListener?.("resize", apply);
    return () => window.removeEventListener?.("resize", apply);
  }, []);

  const dismississedSafe = (dismissed) => {
    // tiny guard so we never throw due to unexpected types
    return dismissed === true;
  };

  const dismissMobileWarning = () => {
    try {
      sessionStorage.setItem(MOBILE_WARNING_KEY, "1");
    } catch {}
    setShowDeviceWarning(false);
  };

  // ✅ Desktop autofocus Step 1 (only when:
  // - not mobile
  // - modal not showing
  // - not currently loading)
  useEffect(() => {
    if (isMobileDevice) return;
    if (showDeviceWarning) return;
    if (loading) return;

    // Slight delay helps ensure layout is settled (prevents scroll quirks)
    const t = window.setTimeout(() => {
      try {
        formTypeRef.current?.focus?.();
      } catch {}
    }, 60);

    return () => window.clearTimeout(t);
  }, [isMobileDevice, showDeviceWarning, loading]);

  // --- Auth watcher: keep fresh ID token in state (same pattern as ContractInsightPage) --- //
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setIdToken(null);
        setIsLoggedIn(false);
        setAuthUser(null);
        return;
      }
      try {
        const token = await user.getIdToken();
        setIdToken(token);
        setIsLoggedIn(true);
        setAuthUser(user);
      } catch (err) {
        console.error("Failed to fetch Firebase ID token for Vision Assist:", err);
        setIdToken(null);
        setIsLoggedIn(false);
        setAuthUser(null);
      }
    });
    return () => unsubscribe();
  }, []);

  // --- Voice billing check (same page-owned pattern used by Doc Writer) --- //
  const fetchBillingSnapshot = async (userOverride = null) => {
    const user = userOverride || authUser || getAuth()?.currentUser || null;
    if (!user) {
      setBillingSnapshot(null);
      setBillingLoading(false);
      setBillingLoaded(false);
      setBillingError("");
      return null;
    }

    setBillingLoading(true);
    setBillingError("");

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${HOST}/user/billing`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data?.error || data?.message || `HTTP ${response.status}`);
      }

      setBillingSnapshot(data || {});
      setBillingLoaded(true);
      return data || {};
    } catch (error) {
      console.error("[VisionAssistLauncher] Billing fetch error:", error);
      setBillingSnapshot(null);
      setBillingLoaded(false);
      setBillingError("Unable to verify your Vision Assist minutes right now.");
      return null;
    } finally {
      setBillingLoading(false);
    }
  };

  useEffect(() => {
    if (!authUser) {
      setBillingSnapshot(null);
      setBillingLoading(false);
      setBillingLoaded(false);
      setBillingError("");
      return;
    }

    fetchBillingSnapshot(authUser);
    
  }, [authUser]);

  const voiceBillingAccess = useMemo(() => {
    const outer = billingSnapshot || {};
    const billing =
      outer.billing && typeof outer.billing === "object" ? outer.billing : outer;

    const monthlyActive = monthlyPlanIsActive(billing);
    const trialActive = trialPlanIsActive(billing);
    const planBucketActive = monthlyActive || trialActive;

    const monthlyEntitlements =
      billing.monthly_entitlements || billing.monthlyEntitlements || {};
    const addonEntitlements =
      billing.addon_entitlements ||
      billing.addonEntitlements ||
      billing.purchased_addons ||
      {};

    // Only the nested buckets are authoritative for Vision Assist.
    const monthlyRead = readEntitlementMinutes(
      monthlyEntitlements,
      "vision_assist_remaining_minutes",
      "vision_assist",
      "visionAssist"
    );
    const addonRead = readEntitlementMinutes(
      addonEntitlements,
      "vision_assist_remaining_minutes",
      "vision_assist",
      "visionAssist"
    );

    const monthlyRemaining = monthlyRead.found ? monthlyRead.value : 0;
    const addonRemaining = addonRead.found ? addonRead.value : 0;
    const usableMonthly = planBucketActive ? monthlyRemaining : 0;
    const available = usableMonthly + addonRemaining;

    let source = "none";
    if (usableMonthly > 0) source = trialActive ? "trial" : "monthly";
    else if (addonRemaining > 0) source = "addon";

    return {
      monthlyActive,
      trialActive,
      monthlyRemaining,
      addonRemaining,
      usableMonthly,
      available,
      source,
      canUseVoice: available > 0,
    };
  }, [billingSnapshot]);

  // Same Doc Writer rule: logged out blocks; logged in must have a positively
  // verified usable billing bucket before recording or paid STT can begin.
  const voiceAccessAllowed = Boolean(
    isLoggedIn &&
      billingLoaded &&
      !billingLoading &&
      !billingError &&
      voiceBillingAccess.canUseVoice
  );

  const voiceAccessLoading = Boolean(
    isLoggedIn && (billingLoading || !billingLoaded || !!billingError)
  );

  const handleVoiceBlocked = (reason) => {
    if (reason === "login_required") {
      navigate("/login", { state: { from: "/vision-assist" } });
      return;
    }

    // Match Doc Writer: fail closed while the existing page billing check is
    // loading or unavailable; do not make a second billing request from the mic.
    if (reason === "billing_loading" || billingError) return;

    if (reason === "billing_required") {
      setVoiceBillingDialogOpen(true);
    }
  };

  useEffect(() => {
    if (!isLoggedIn || voiceAccessAllowed) {
      setVoiceBillingDialogOpen(false);
    }
  }, [isLoggedIn, voiceAccessAllowed]);

  const openVoiceBilling = () => {
    if (onIOSApp) {
      window.open("https://www.asklawly.ai", "_blank", "noopener,noreferrer");
      return;
    }
    navigate("/billing");
  };

  // --- Step 3 voice recorder/transcription (Doc Writer-compatible behavior) --- //
  useEffect(() => {
    const STYLE_ID = "asklawly-vision-launcher-voice-keyframes";
    if (typeof document === "undefined") return;
    if (document.getElementById(STYLE_ID)) return;

    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.type = "text/css";
    style.textContent = `
@keyframes asklawlyVisionLauncherSpin360 {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;
    document.head.appendChild(style);
  }, []);

  useEffect(() => {
    if (isTranscribing || isAcceptingVoice) return undefined;
    if (!recording && !voiceReady) return undefined;

    const interval = window.setInterval(() => {
      const sample = isFlatRef.current ? 2 : Math.random() * 20;
      setWaveformData((data) => [...data.slice(-100), sample]);
    }, 60);

    return () => window.clearInterval(interval);
  }, [recording, voiceReady, isTranscribing, isAcceptingVoice]);

  useEffect(() => {
    if (!voiceNotice) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setVoiceNotice(null);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [voiceNotice]);

  useEffect(() => {
    return () => {
      try {
        mediaRecorderRef.current?.stop?.();
      } catch {}
      mediaRecorderRef.current = null;

      try {
        streamRef.current?.getTracks?.().forEach((track) => {
          try {
            track.stop();
          } catch {}
        });
      } catch {}
      streamRef.current = null;
    };
  }, []);

  const showVoiceNotice = (
    message,
    { kind = "info", title = "Voice input", dedupeKey = "" } = {}
  ) => {
    const clean = String(message || "").trim();
    if (!clean) return;

    const now = Date.now();
    const key = String(dedupeKey || clean);
    const last = lastVoiceNoticeRef.current || { key: "", at: 0 };
    if (last.key === key && now - Number(last.at || 0) < 1200) return;
    lastVoiceNoticeRef.current = { key, at: now };

    setVoiceNotice({ message: clean, kind, title });
  };

  const closeVoiceNotice = () => setVoiceNotice(null);

  const safeVoiceAction = (fn) => {
    const now = Date.now();
    if (now - (lastVoiceActionAtRef.current || 0) < 250) return;
    lastVoiceActionAtRef.current = now;
    fn?.();
  };

  const voiceAccessIsReady = () => {
    if (!isLoggedIn || !authUser) {
      handleVoiceBlocked("login_required");
      return false;
    }

    if (voiceAccessLoading) {
      handleVoiceBlocked("billing_loading");
      return false;
    }

    if (!voiceAccessAllowed) {
      handleVoiceBlocked("billing_required");
      return false;
    }

    return true;
  };

  const pickBestMimeType = () => {
    const candidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/mp4",
      "audio/mpeg",
    ];

    for (const type of candidates) {
      try {
        if (
          typeof window !== "undefined" &&
          window.MediaRecorder &&
          MediaRecorder.isTypeSupported(type)
        ) {
          return type;
        }
      } catch {}
    }

    return "";
  };

  const stopTracks = (stream) => {
    try {
      stream?.getTracks?.().forEach((track) => {
        try {
          track.stop();
        } catch {}
      });
    } catch {}
  };

  const resetVoiceState = () => {
    setRecording(false);
    setVoiceReady(false);
    setVoiceText("");
    setWaveformData([]);
    isFlatRef.current = true;

    recordedChunksRef.current = [];
    recordedBlobRef.current = null;
    recordedMimeRef.current = "";
    pendingAcceptRef.current = false;
    stopPromiseRef.current = null;
    recordStartAtRef.current = 0;
    setIsAcceptingVoice(false);
  };

  const ensureRecorderStopped = () => {
    try {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === "inactive") return Promise.resolve();
      if (stopPromiseRef.current) return stopPromiseRef.current;

      stopPromiseRef.current = new Promise((resolve) => {
        const previousOnStop = recorder.onstop;
        recorder.onstop = (event) => {
          try {
            previousOnStop?.(event);
          } catch {}
          resolve();
          stopPromiseRef.current = null;
        };

        try {
          recorder.requestData?.();
        } catch {}
        try {
          recorder.stop();
        } catch {
          resolve();
          stopPromiseRef.current = null;
        }
      });

      return stopPromiseRef.current;
    } catch {
      return Promise.resolve();
    }
  };

  const clampTextPosition = (value, position) => {
    const length = String(value || "").length;
    const numeric = Number(position);
    if (!Number.isFinite(numeric)) return length;
    return Math.max(0, Math.min(length, Math.trunc(numeric)));
  };

  const rememberGoalSelection = (element = goalRef.current) => {
    const value = String(
      element?.value !== undefined ? element.value : goal || ""
    );
    const fallback = value.length;
    const start = clampTextPosition(
      value,
      Number.isInteger(element?.selectionStart)
        ? element.selectionStart
        : goalSelectionRef.current?.start
    );
    const end = clampTextPosition(
      value,
      Number.isInteger(element?.selectionEnd)
        ? element.selectionEnd
        : goalSelectionRef.current?.end
    );

    goalSelectionRef.current = {
      start: Number.isFinite(start) ? start : fallback,
      end: Number.isFinite(end) ? Math.max(start, end) : fallback,
    };
    return goalSelectionRef.current;
  };

  const trimVoiceOverlapAgainstLeftContext = (left = "", transcript = "") => {
    const source = String(transcript || "").trim();
    if (!source) return "";

    const maxCheck = Math.min(50, left.length, source.length);
    for (let k = maxCheck; k > 0; k -= 1) {
      if (left.slice(-k).toLowerCase() === source.slice(0, k).toLowerCase()) {
        return source.slice(k);
      }
    }
    return source;
  };

  const prepareVoiceInsertion = (left = "", right = "", transcript = "") => {
    let insertion = trimVoiceOverlapAgainstLeftContext(left, transcript);
    if (!insertion) return "";

    const leftChar = left.slice(-1);
    const rightChar = right.slice(0, 1);
    const firstInserted = insertion.slice(0, 1);
    const lastInserted = insertion.slice(-1);

    if (
      leftChar &&
      !/\s/.test(leftChar) &&
      firstInserted &&
      !/^[,.;:!?)}\]]$/.test(firstInserted)
    ) {
      insertion = ` ${insertion}`;
    }

    if (
      rightChar &&
      !/\s/.test(rightChar) &&
      lastInserted &&
      !/[\s([{<]$/.test(lastInserted) &&
      !/^[,.;:!?)}\]]$/.test(rightChar)
    ) {
      insertion = `${insertion} `;
    }

    return insertion;
  };

  const restoreGoalCaretSoon = () => {
    const restore = () => {
      const element = goalRef.current;
      const pending = pendingGoalCaretRef.current;
      if (!element || !Number.isFinite(pending)) return false;

      const caret = clampTextPosition(element.value || "", pending);
      try {
        element.focus({ preventScroll: true });
      } catch {
        element.focus?.();
      }
      element.setSelectionRange?.(caret, caret);
      goalSelectionRef.current = { start: caret, end: caret };
      pendingGoalCaretRef.current = null;
      return true;
    };

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (!restore()) window.setTimeout(restore, 0);
      });
    });
  };

  const insertVoiceTextAtGoalCaret = (transcript) => {
    const existing = String(goal || "");
    const saved = goalSelectionRef.current || {};
    const fallback = existing.length;
    const start = clampTextPosition(
      existing,
      Number.isFinite(saved.start) ? saved.start : fallback
    );
    const end = clampTextPosition(
      existing,
      Number.isFinite(saved.end) ? saved.end : start
    );
    const selectionStart = Math.min(start, end);
    const selectionEnd = Math.max(start, end);

    const left = existing.slice(0, selectionStart);
    const right = existing.slice(selectionEnd);
    const prepared = prepareVoiceInsertion(left, right, transcript);
    const preservedLength =
      existing.length - Math.max(0, selectionEnd - selectionStart);
    const available = Math.max(0, GOAL_MAX_LENGTH - preservedLength);
    const accepted = prepared.slice(0, available);
    const rejectedCount = Math.max(0, prepared.length - accepted.length);
    const nextValue = left + accepted + right;
    const nextCaret = left.length + accepted.length;

    setGoal(nextValue);
    if (goalError) setGoalError(null);
    goalSelectionRef.current = { start: nextCaret, end: nextCaret };
    pendingGoalCaretRef.current = nextCaret;

    if (rejectedCount > 0) {
      setGoalLimitNotice(
        `What do you want help with? accepts up to ${GOAL_MAX_LENGTH.toLocaleString()} characters. ` +
          `${rejectedCount.toLocaleString()} transcribed character${
            rejectedCount === 1 ? " was" : "s were"
          } not added.`
      );
    } else {
      setGoalLimitNotice("");
    }
  };

  const getIdTokenSafe = async () => {
    try {
      const user = authUser || getAuth()?.currentUser || null;
      if (!user?.getIdToken) return null;
      return await user.getIdToken();
    } catch (error) {
      console.warn("[VisionAssistLauncher] getIdToken failed:", error);
      return null;
    }
  };

  const transcribeAndAppend = async (blob, mimeType) => {
    // Re-check immediately before the paid STT request, exactly like Doc Writer.
    if (!voiceAccessIsReady()) {
      setIsAcceptingVoice(false);
      return;
    }

    if (!HOST) {
      showVoiceNotice("Backend URL not configured (REACT_APP_BACKEND_URL).", {
        kind: "error",
        dedupeKey: "vision-launcher-voice-backend-url",
      });
      setIsAcceptingVoice(false);
      return;
    }

    if (!blob || blob.size <= 0) {
      showVoiceNotice("No audio captured. Try again.", {
        kind: "warning",
        dedupeKey: "vision-launcher-voice-no-audio",
      });
      setIsAcceptingVoice(false);
      return;
    }

    if (blob.size < MIN_BLOB_BYTES) {
      showVoiceNotice(
        "Audio too quiet/short. Please speak a bit longer and try again.",
        {
          kind: "warning",
          dedupeKey: "vision-launcher-voice-audio-too-short-transcribe",
        }
      );
      setIsAcceptingVoice(false);
      return;
    }

    const token = await getIdTokenSafe();
    if (!token) {
      handleVoiceBlocked("login_required");
      setIsAcceptingVoice(false);
      return;
    }

    setIsTranscribing(true);
    setVoiceText("");

    try {
      const ext = (mimeType || "").includes("mp4")
        ? "m4a"
        : (mimeType || "").includes("mpeg")
        ? "mp3"
        : "webm";

      const file = new File([blob], `speech.${ext}`, {
        type: mimeType || "audio/webm",
      });

      const formData = new FormData();
      formData.append("file", file);
      formData.append("language", "auto");
      formData.append("sessionId", "vision-assist-launcher");
      formData.append("sessionKind", "fast_chat");
      formData.append("userEmail", authUser?.email || "");
      formData.append("userType", "individual");

      console.log("[STT][vision-launcher] uploading:", {
        url: `${HOST}/stt/transcribe`,
        size: blob.size,
        mime: mimeType || "audio/webm",
        filename: `speech.${ext}`,
      });

      const response = await fetch(`${HOST}/stt/transcribe`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.ok) {
        const reason = data?.reason || data?.error || `stt_failed_${response.status}`;
        throw new Error(reason);
      }

      const transcript = String(data?.text || data?.transcript || "").trim();
      if (!transcript) throw new Error("empty_transcript");

      setVoiceText(transcript);
      insertVoiceTextAtGoalCaret(transcript);
      resetVoiceState();
      restoreGoalCaretSoon();
    } catch (error) {
      console.error("[VisionAssistLauncher] Transcribe failed:", error);
      showVoiceNotice(
        `Voice transcription failed (${String(error?.message || error)}). Please try again.`,
        {
          kind: "error",
          dedupeKey: `vision-launcher-voice-transcription-${String(
            error?.message || error
          )}`,
        }
      );
      setVoiceReady(true);
    } finally {
      setIsTranscribing(false);
      setIsAcceptingVoice(false);
    }
  };

  const startRecording = async () => {
    if (!voiceAccessIsReady()) return;
    if (isTranscribing || isAcceptingVoice || recording) return;

    if (!navigator?.mediaDevices?.getUserMedia) {
      showVoiceNotice("Microphone not supported on this device/browser.", {
        kind: "warning",
        dedupeKey: "vision-launcher-voice-mic-unsupported",
      });
      return;
    }

    recordedChunksRef.current = [];
    recordedBlobRef.current = null;
    recordedMimeRef.current = "";
    setVoiceReady(false);
    setVoiceText("");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;

      const mimeType = pickBestMimeType();
      recordedMimeRef.current = mimeType || "";
      const recorder = new MediaRecorder(
        stream,
        mimeType ? { mimeType } : undefined
      );
      mediaRecorderRef.current = recorder;

      recorder.onstart = () => {
        setRecording(true);
        setVoiceReady(false);
        setWaveformData([]);
        isFlatRef.current = false;
        recordStartAtRef.current = Date.now();
      };

      recorder.ondataavailable = (event) => {
        if (event?.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const chunks = recordedChunksRef.current || [];
        const type =
          recordedMimeRef.current || chunks?.[0]?.type || "audio/webm";
        const blob = new Blob(chunks, { type });

        stopTracks(streamRef.current);
        streamRef.current = null;
        mediaRecorderRef.current = null;
        setRecording(false);
        isFlatRef.current = true;
        recordedBlobRef.current = blob;

        const durationMs = recordStartAtRef.current
          ? Date.now() - recordStartAtRef.current
          : 0;

        console.log("[STT][vision-launcher] onstop:", {
          durationMs,
          size: blob?.size || 0,
          mime: type,
          chunks: chunks?.length || 0,
        });

        if (!blob || blob.size <= 0) {
          setVoiceReady(false);
          recordedBlobRef.current = null;
          recordedChunksRef.current = [];
          showVoiceNotice(
            "No audio captured. Please try again and speak a little longer.",
            {
              kind: "warning",
              dedupeKey: "vision-launcher-voice-no-audio-longer",
            }
          );
          setIsAcceptingVoice(false);
          return;
        }

        if (durationMs > 0 && durationMs < MIN_RECORD_MS) {
          setVoiceReady(false);
          recordedBlobRef.current = null;
          recordedChunksRef.current = [];
          showVoiceNotice(
            "Recording too short. Please speak for at least 1 second and try again.",
            {
              kind: "warning",
              dedupeKey: "vision-launcher-voice-recording-too-short",
            }
          );
          setIsAcceptingVoice(false);
          return;
        }

        if (blob.size < MIN_BLOB_BYTES) {
          setVoiceReady(false);
          recordedBlobRef.current = null;
          recordedChunksRef.current = [];
          showVoiceNotice(
            "Audio too quiet/short. Please speak a bit longer and try again.",
            {
              kind: "warning",
              dedupeKey: "vision-launcher-voice-audio-too-short-stop",
            }
          );
          setIsAcceptingVoice(false);
          return;
        }

        setVoiceReady(true);

        if (pendingAcceptRef.current) {
          pendingAcceptRef.current = false;
          transcribeAndAppend(blob, type);
        }
      };

      recorder.start(250);
    } catch (error) {
      console.warn("[VisionAssistLauncher] Mic start error:", error);
      resetVoiceState();
      showVoiceNotice(
        "Microphone access is blocked or unavailable. Please allow microphone permission and try again.",
        {
          kind: "warning",
          dedupeKey: "vision-launcher-voice-mic-permission",
        }
      );
    }
  };

  const stopRecording = async () => {
    try {
      try {
        mediaRecorderRef.current?.requestData?.();
      } catch {}
      await new Promise((resolve) => window.setTimeout(resolve, 180));
      await ensureRecorderStopped();
    } catch (error) {
      console.warn("[VisionAssistLauncher] stopRecording failed:", error);
    }
  };

  const cancelVoice = () => {
    safeVoiceAction(() => {
      if (isTranscribing) return;

      try {
        const recorder = mediaRecorderRef.current;
        if (recorder && recorder.state !== "inactive") {
          recorder.ondataavailable = null;
          recorder.onstop = null;
          recorder.stop();
        }
      } catch {}
      mediaRecorderRef.current = null;

      stopTracks(streamRef.current);
      streamRef.current = null;

      setIsAcceptingVoice(false);
      const saved = goalSelectionRef.current || {};
      pendingGoalCaretRef.current = Number.isFinite(saved.start)
        ? saved.start
        : null;
      resetVoiceState();
      restoreGoalCaretSoon();
    });
  };

  const handleVoiceInput = () => {
    safeVoiceAction(() => {
      rememberGoalSelection();
      if (!voiceAccessIsReady()) return;
      if (recording || isTranscribing || isAcceptingVoice) return;

      if (voiceReady) resetVoiceState();
      startRecording();
    });
  };

  const acceptVoice = () => {
    safeVoiceAction(async () => {
      if (isTranscribing || isAcceptingVoice) return;
      if (!voiceAccessIsReady()) return;

      setIsAcceptingVoice(true);

      try {
        if (recording) {
          const durationMs = recordStartAtRef.current
            ? Date.now() - recordStartAtRef.current
            : 0;

          if (durationMs > 0 && durationMs < MIN_RECORD_MS) {
            showVoiceNotice(
              "Recording too short. Please speak for at least 1 second.",
              {
                kind: "warning",
                dedupeKey: "vision-launcher-voice-recording-too-short-accept",
              }
            );
            setIsAcceptingVoice(false);
            return;
          }

          pendingAcceptRef.current = true;
          await stopRecording();
          return;
        }

        if (voiceReady && recordedBlobRef.current) {
          await transcribeAndAppend(
            recordedBlobRef.current,
            recordedMimeRef.current || recordedBlobRef.current.type
          );
          return;
        }

        setIsAcceptingVoice(false);
      } catch (error) {
        console.warn("[VisionAssistLauncher] acceptVoice failed:", error);
        setIsAcceptingVoice(false);
      }
    });
  };

  const showVoiceWave =
    recording || voiceReady || isTranscribing || isAcceptingVoice;

  const Spinner = ({ size = 16 }) => (
    <span
      aria-label="Loading"
      style={{
        width: size,
        height: size,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        willChange: "transform",
        WebkitBackfaceVisibility: "hidden",
        backfaceVisibility: "hidden",
        transform: "translateZ(0)",
        WebkitTransform: "translateZ(0)",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: size,
          height: size,
          display: "block",
          borderRadius: "50%",
          border: "2px solid rgba(17,24,39,0.20)",
          borderTopColor: "#111827",
          animation: "asklawlyVisionLauncherSpin360 0.72s linear infinite",
        }}
      />
    </span>
  );

  // --- Helper: classify billing-related error strings from backend --- //
  const classifyBillingNotice = (rawMessage) => {
    if (!rawMessage) return null;
    const lower = String(rawMessage).toLowerCase();

    if (lower.includes("vision assist") && lower.includes("minutes")) return "billing";
    if (lower.includes("trial plan has expired")) return "billing";
    if (lower.includes("trial has expired")) return "billing";
    if (lower.includes("monthly plan has expired")) return "billing";
    if (lower.includes("plan has expired")) return "billing";
    if (lower.includes("subscription has expired")) return "billing";

    return null;
  };

  // --- Helper: write optional launch context to sessionStorage (for session page + auto-greet) --- //
  const storeLaunchPayload = (sessionId) => {
    if (!sessionId) return;

    const payload = {
      v: 1,
      session_id: sessionId,
      launched_at_ms: Date.now(),
      source: "launcher",

      // user inputs (trimmed)
      formType: formType.trim(),
      siteHost: siteHost.trim(),
      goal: goal.trim(),
    };

    try {
      sessionStorage.setItem(`va_launch_payload:${sessionId}`, JSON.stringify(payload));
    } catch {}

    try {
      sessionStorage.setItem("va_launch_payload_latest", JSON.stringify(payload));
    } catch {}
  };

  const focusFirstRequiredField = (fieldRef) => {
    window.setTimeout(() => {
      const field = fieldRef?.current;
      if (!field) return;

      field.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      window.setTimeout(() => {
        try {
          field.focus({ preventScroll: true });
        } catch {
          field.focus?.();
        }
      }, 240);
    }, 20);
  };

  // --- Main handler: Start Vision Assist session --- //
  const handleStartClick = async (evt) => {
    evt.preventDefault();

    setFormTypeError(null);
    setGoalError(null);
    setTermsError(null);
    setSessionInfo(null);

    if (notice && notice.type !== "ok" && notice.type !== "billing") {
      setNotice(null);
    }

    if (!isLoggedIn || !idToken) {
      navigate("/login", { state: { from: "/vision-assist" } });
      return;
    }

    let hasError = false;
    let firstRequiredFieldRef = null;

    if (!formType.trim()) {
      setFormTypeError("Please tell us what you’re working on.");
      firstRequiredFieldRef = firstRequiredFieldRef || formTypeRef;
      hasError = true;
    }

    if (!goal.trim()) {
      setGoalError("Please tell us what you want help with.");
      firstRequiredFieldRef = firstRequiredFieldRef || goalRef;
      hasError = true;
    }

    if (!agreedToTerms) {
      setTermsError("Please agree to the Terms of Use and Privacy Policy.");
      firstRequiredFieldRef = firstRequiredFieldRef || termsRef;
      hasError = true;
    }

    if (hasError) {
      focusFirstRequiredField(firstRequiredFieldRef);
      return;
    }

    try {
      setLoading(true);

      const data = await startVisionSession(
        {
          formType: formType.trim(),
          siteHost: siteHost.trim(),
          goal: goal.trim(),
        },
        { idToken }
      );

      setSessionInfo(data || null);

      if (data?.session_id) {
        setNotice({ type: "ok", msg: "Vision Assist session started. Redirecting…" });

        storeLaunchPayload(data.session_id);

        // Vision sessions are persistent resources. The launcher creates the
        // session, stores optional launcher context, then opens the same canonical
        // session route used by Sidebar history.
        navigate(`/vision/session/${encodeURIComponent(data.session_id)}`, {
          state: { va_from_launcher: true },
        });
        return;
      }

      setNotice({
        type: "error",
        msg: "Session started but session_id was missing from the response.",
      });
    } catch (err) {
      console.error("Vision Assist start failed:", err);

      const backendMsg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Unable to start Vision Assist session. Please try again.";

      const billingType = classifyBillingNotice(backendMsg);

      setNotice({
        type: billingType || "error",
        msg: String(backendMsg || "").trim(),
      });
    } finally {
      setLoading(false);
    }
  };

  const starting = loading;

  const pageStyle = {
    ...styles.outerWrap,
    ...(isSmallScreen ? styles.outerWrapMobile : null),
  };

  const pageHeaderStyle = {
    ...styles.pageHeader,
  };

  const eyebrowStyle = {
    ...styles.eyebrow,
    ...(isSmallScreen ? styles.eyebrowMobile : null),
  };

  const titleStyle = {
    ...styles.h1,
    ...(isSmallScreen ? styles.h1Mobile : null),
  };

  const subtitleStyle = {
    ...styles.subtitle,
    ...(isSmallScreen ? styles.subtitleMobile : null),
  };

  const featureGridStyle = {
    ...styles.featureGrid,
    ...(isSmallScreen ? styles.featureGridMobile : null),
  };

  const pageGridStyle = {
    ...styles.pageGrid,
    ...(isSmallScreen ? styles.pageGridMobile : null),
  };

  const inputStyle = (fieldName) => ({
    ...styles.input,
    ...(focusedField === fieldName ? styles.inputFocus : null),
  });

  return (
    <div className="vision-assist-page-root" style={pageStyle}>
      <style>{`
        .vision-assist-page-root * {
          box-sizing: border-box;
        }

        .vision-assist-page-root button,
        .vision-assist-page-root input,
        .vision-assist-page-root textarea {
          font: inherit;
        }

        .vision-assist-page-root a {
          color: #2563EB;
          font-weight: 900;
          text-decoration: none;
        }

        .vision-assist-page-root a:hover {
          text-decoration: underline;
        }

        .vision-assist-page-root button:focus-visible,
        .vision-assist-page-root input:focus-visible,
        .vision-assist-page-root textarea:focus-visible {
          outline: 3px solid rgba(37,99,235,0.22);
          outline-offset: 2px;
        }

        html, body, #root {
          max-width: 100%;
          overflow-x: hidden;
        }

        .vision-assist-page-root,
        .vision-assist-page-root *,
        .vision-assist-feature-grid,
        .vision-assist-page-grid,
        .vision-assist-workflow-panel {
          min-width: 0;
          max-width: 100%;
        }

        @media (max-width: 1023px) {
          .vision-assist-page-root {
            padding: 14px 12px 34px !important;
          }
        }

        @media (max-width: 640px) {
          .vision-assist-step-header {
            align-items: flex-start !important;
          }

          .vision-assist-step-body {
            padding-top: 13px !important;
          }
        }
      `}</style>

      {voiceBillingDialogOpen ? (
        <div
          style={styles.voiceBillingOverlay}
          role="dialog"
          aria-modal="true"
          aria-labelledby="vision-assist-voice-billing-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setVoiceBillingDialogOpen(false);
            }
          }}
        >
          <div style={styles.voiceBillingCard}>
            <button
              type="button"
              aria-label="Close billing message"
              title="Close"
              onClick={() => setVoiceBillingDialogOpen(false)}
              style={styles.voiceBillingClose}
            >
              ×
            </button>

            <div style={styles.voiceBillingIcon} aria-hidden="true">
              ◉
            </div>

            <div style={styles.voiceBillingEyebrow}>Vision Assist voice input</div>

            <h2
              id="vision-assist-voice-billing-title"
              style={styles.voiceBillingTitle}
            >
              No Vision Assist minutes are currently available
            </h2>

            <p style={styles.voiceBillingText}>
              No usable Vision Assist minutes are available from your active
              monthly or trial allowance or from purchased add-on minutes.
            </p>

            <div style={styles.voiceBillingActions}>
              <button
                type="button"
                onClick={() => setVoiceBillingDialogOpen(false)}
                style={styles.voiceBillingSecondaryButton}
              >
                Not now
              </button>
              <button
                type="button"
                onClick={() => {
                  setVoiceBillingDialogOpen(false);
                  openVoiceBilling();
                }}
                style={styles.voiceBillingPrimaryButton}
              >
                {onIOSApp ? "Manage account" : "Open Billing"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {voiceNotice ? (
        <div
          style={styles.voiceNoticeBackdrop}
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeVoiceNotice();
          }}
        >
          <div
            style={styles.voiceNoticeCard}
            role="dialog"
            aria-modal="true"
            aria-labelledby="vision-launcher-voice-notice-title"
            aria-describedby="vision-launcher-voice-notice-message"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div style={styles.voiceNoticeHeader}>
              <h3
                id="vision-launcher-voice-notice-title"
                style={styles.voiceNoticeTitle}
              >
                {voiceNotice.title || "Voice input"}
              </h3>
              <button
                type="button"
                style={styles.voiceNoticeClose}
                onClick={closeVoiceNotice}
                aria-label="Close voice message"
                title="Close"
              >
                <FaTimes />
              </button>
            </div>

            <div
              id="vision-launcher-voice-notice-message"
              style={styles.voiceNoticeBody}
            >
              {voiceNotice.message}
            </div>

            <div style={styles.voiceNoticeFooter}>
              <button
                type="button"
                style={styles.voiceNoticeOkay}
                onClick={closeVoiceNotice}
                autoFocus
              >
                OK
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showDeviceWarning && (
        <div style={styles.deviceOverlay} role="dialog" aria-modal="true">
          <div style={styles.deviceModal}>
            <div style={styles.deviceTitle}>Best on laptop or desktop</div>

            <div style={styles.deviceBody}>
              You can continue on mobile for <strong>chat</strong> and{" "}
              <strong>file uploads</strong>. Screen sharing may not work reliably
              in mobile browsers. For the best experience, use a{" "}
              <strong>laptop/desktop</strong> with <strong>Chrome</strong> or{" "}
              <strong>Microsoft Edge</strong>.
            </div>

            <div style={styles.deviceActions}>
              <button
                type="button"
                style={styles.deviceBtn}
                onClick={dismissMobileWarning}
              >
                OK — Continue
              </button>
            </div>

            <div style={styles.deviceFootnote}>
              If screen sharing fails, you can still upload screenshots or
              documents and keep going.
            </div>
          </div>
        </div>
      )}

      <header style={pageHeaderStyle}>
        <div style={styles.pageHeaderText}>
          <div style={eyebrowStyle}>Vision Assist</div>
          <h1 style={titleStyle}>Start a guided session</h1>
          <p style={subtitleStyle}>
            Start a real-time session for screen guidance, form help, and
            step-by-step assistance.
          </p>
        </div>
      </header>

      <div
        className="vision-assist-feature-grid"
        style={featureGridStyle}
      >
        <FeatureTile
          icon="▣"
          title="On-screen guidance"
          text="Analyze your screen or uploads and explain fields in plain language."
        />
        <FeatureTile
          icon="▤"
          title="Professional workflow"
          text="Built for tax filing, business formation, portals, and document tasks."
        />
        <FeatureTile
          icon="◷"
          title="Usage & control"
          text="Uses Vision minutes from your plan. End the session any time."
        />
      </div>

      <div className="vision-assist-page-grid" style={pageGridStyle}>
        <main style={styles.mainColumn}>
          <form onSubmit={handleStartClick}>
            <section
              className="vision-assist-workflow-panel"
              style={styles.workflowPanel}
            >
              <FormStep
                number="1"
                title="What are you working on?"
                required
              >
                <input
                  ref={formTypeRef}
                  style={inputStyle("formType")}
                  type="text"
                  placeholder="Federal/state tax return, sales tax filing, USCIS/visa application, business registration, court filing…"
                  value={formType}
                  onFocus={() => setFocusedField("formType")}
                  onBlur={() => setFocusedField(null)}
                  onChange={(event) => {
                    setFormType(event.target.value);
                    if (formTypeError) setFormTypeError(null);
                  }}
                  aria-required="true"
                  aria-invalid={Boolean(formTypeError)}
                />
                {formTypeError ? (
                  <div style={styles.inlineError}>{formTypeError}</div>
                ) : null}

                <div style={styles.helperLine}>
                  <span style={styles.infoDot}>i</span>
                  <span>
                    Tell us the task, form, filing, application, or workflow you’re working on.
                  </span>
                </div>
              </FormStep>

              <FormStep
                number="2"
                title="Website / portal"
                optional
              >
                <input
                  style={inputStyle("siteHost")}
                  type="text"
                  placeholder="irs.gov, ftb.ca.gov, uscis.gov, sos.ca.gov, court or government website…"
                  value={siteHost}
                  onFocus={() => setFocusedField("siteHost")}
                  onBlur={() => setFocusedField(null)}
                  onChange={(event) => setSiteHost(event.target.value)}
                />

                <div style={styles.helperLine}>
                  <span style={styles.infoDot}>i</span>
                  <span>Host name only — no usernames or passwords.</span>
                </div>
              </FormStep>

              <FormStep
                number="3"
                title="What do you want help with?"
                required
              >
                <div style={styles.voiceShell}>
                  {showVoiceWave ? (
                    <div style={styles.voiceWave}>
                      <div style={styles.voiceBars}>
                        {waveformData.map((value, index) => (
                          <div
                            key={index}
                            style={{
                              flex: 1,
                              height: `${value * 2}px`,
                              backgroundColor: "#555",
                              marginTop: `${10 - value / 2}px`,
                              marginBottom: `${10 - value / 2}px`,
                              borderRadius: 1,
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <textarea
                      ref={goalRef}
                      style={{
                        ...inputStyle("goal"),
                        ...styles.textarea,
                        ...styles.voiceTextarea,
                      }}
                      placeholder="For example: Explain each field, tell me what information or documents I may need, and guide me through the process step by step."
                      value={goal}
                      maxLength={GOAL_MAX_LENGTH}
                      onFocus={(event) => {
                        setFocusedField("goal");
                        rememberGoalSelection(event.currentTarget);
                      }}
                      onBlur={(event) => {
                        rememberGoalSelection(event.currentTarget);
                        setFocusedField(null);
                      }}
                      onSelect={(event) => rememberGoalSelection(event.currentTarget)}
                      onClick={(event) => rememberGoalSelection(event.currentTarget)}
                      onKeyUp={(event) => rememberGoalSelection(event.currentTarget)}
                      onPaste={(event) => {
                        const pastedText = event.clipboardData?.getData("text");
                        if (typeof pastedText !== "string") return;

                        const target = event.currentTarget;
                        const currentValue = String(goal || "");
                        const selectionStart = Number.isInteger(target.selectionStart)
                          ? target.selectionStart
                          : currentValue.length;
                        const selectionEnd = Number.isInteger(target.selectionEnd)
                          ? target.selectionEnd
                          : selectionStart;
                        const selectedLength = Math.max(0, selectionEnd - selectionStart);
                        const projectedLength =
                          currentValue.length - selectedLength + pastedText.length;

                        if (projectedLength <= GOAL_MAX_LENGTH) {
                          setGoalLimitNotice("");
                          return;
                        }

                        event.preventDefault();
                        const available = Math.max(
                          0,
                          GOAL_MAX_LENGTH - (currentValue.length - selectedLength)
                        );
                        const acceptedText = pastedText.slice(0, available);
                        const rejectedCount = pastedText.length - acceptedText.length;
                        const nextValue =
                          currentValue.slice(0, selectionStart) +
                          acceptedText +
                          currentValue.slice(selectionEnd);
                        const nextCaret = selectionStart + acceptedText.length;

                        setGoal(nextValue);
                        setGoalLimitNotice(
                          `The field limit is ${GOAL_MAX_LENGTH.toLocaleString()} characters. ` +
                            `${rejectedCount.toLocaleString()} pasted character${
                              rejectedCount === 1 ? " was" : "s were"
                            } not added.`
                        );
                        goalSelectionRef.current = {
                          start: nextCaret,
                          end: nextCaret,
                        };
                        pendingGoalCaretRef.current = nextCaret;
                        window.requestAnimationFrame(() => {
                          target.focus();
                          target.setSelectionRange?.(nextCaret, nextCaret);
                        });
                      }}
                      onChange={(event) => {
                        const next = String(event.target.value || "").slice(
                          0,
                          GOAL_MAX_LENGTH
                        );
                        setGoal(next);
                        setGoalLimitNotice("");
                        rememberGoalSelection(event.currentTarget);
                        if (goalError) setGoalError(null);
                      }}
                      aria-required="true"
                      aria-invalid={Boolean(goalError)}
                    />
                  )}

                  <div style={styles.voiceActions}>
                    {showVoiceWave ? (
                      <>
                        <button
                          onPointerDown={cancelVoice}
                          type="button"
                          style={{
                            ...styles.voiceButton,
                            cursor:
                              isTranscribing || isAcceptingVoice
                                ? "not-allowed"
                                : "pointer",
                            opacity:
                              isTranscribing || isAcceptingVoice ? 0.55 : 1,
                          }}
                          title="Cancel voice"
                          aria-label="Cancel voice"
                          disabled={isTranscribing || isAcceptingVoice}
                        >
                          <FaTimes />
                        </button>

                        <button
                          onPointerDown={acceptVoice}
                          type="button"
                          style={{
                            ...styles.voiceButton,
                            cursor:
                              isTranscribing ||
                              isAcceptingVoice ||
                              (!recording && !voiceReady)
                                ? "not-allowed"
                                : "pointer",
                            opacity:
                              isTranscribing ||
                              isAcceptingVoice ||
                              (!recording && !voiceReady)
                                ? 0.55
                                : 1,
                          }}
                          title={recording ? "Stop & transcribe" : "Transcribe"}
                          aria-label={
                            recording ? "Stop and transcribe" : "Transcribe"
                          }
                          disabled={
                            isTranscribing ||
                            isAcceptingVoice ||
                            (!recording && !voiceReady)
                          }
                        >
                          {isTranscribing || isAcceptingVoice ? (
                            <Spinner size={16} />
                          ) : (
                            <FaCheck />
                          )}
                        </button>
                      </>
                    ) : (
                      <button
                        onPointerDown={handleVoiceInput}
                        type="button"
                        style={{
                          ...styles.voiceButton,
                          opacity: voiceAccessLoading ? 0.65 : 1,
                        }}
                        title="Speak"
                        aria-label="Speak into What do you want help with?"
                        disabled={isTranscribing || isAcceptingVoice}
                      >
                        <FiMic />
                      </button>
                    )}
                  </div>
                </div>

                {(isTranscribing || isAcceptingVoice) && (
                  <div style={styles.voiceHint}>Transcribing…</div>
                )}

                {!!voiceText && !showVoiceWave && (
                  <div style={styles.voiceHint}>
                    <span style={{ opacity: 0.9 }}>Last voice:</span>{" "}
                    {voiceText}
                  </div>
                )}

                {goalError ? (
                  <div style={styles.inlineError}>{goalError}</div>
                ) : null}

                {goalLimitNotice ? (
                  <div style={styles.goalLimitNotice}>{goalLimitNotice}</div>
                ) : null}

                <div style={styles.goalMetaRow}>
                  <div style={styles.helperLineCompact}>
                    <span style={styles.infoDot}>i</span>
                    <span>
                      Be specific about what you want to accomplish. You can describe
                      questions, confusing fields, documents you have, or the step where
                      you need help. Maximum {GOAL_MAX_LENGTH.toLocaleString()} characters.
                    </span>
                  </div>
                  <div style={styles.goalCounter}>
                    {goal.length.toLocaleString()} / {GOAL_MAX_LENGTH.toLocaleString()}
                  </div>
                </div>

                <label style={styles.termsLabel}>
                  <input
                    ref={termsRef}
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(event) => {
                      setAgreedToTerms(event.target.checked);
                      if (termsError) setTermsError(null);
                    }}
                    style={styles.termsCheckbox}
                  />
                  <span>
                    I agree to the <a href="/terms">Terms of Use</a> and{" "}
                    <a href="/privacy">Privacy Policy</a>.
                  </span>
                </label>

                {termsError ? (
                  <div style={styles.inlineError}>{termsError}</div>
                ) : null}

                {notice ? (
                  <div
                    className="va-notice"
                    style={{
                      ...styles.notice,
                      ...(notice.type === "ok"
                        ? styles.noticeOk
                        : notice.type === "billing"
                          ? styles.noticeBilling
                          : styles.noticeError),
                    }}
                  >
                    <p style={styles.noticeText}>{notice.msg}</p>
                    {notice.type === "billing" ? (
                      <p style={styles.noticeSubText}>
                        To continue, please check your billing and plan details
                        on the <a href="/billing">Billing page</a>.
                        {onIOSApp
                          ? " On iOS, open www.asklawly.com in your browser and sign in to manage your plan."
                          : ""}
                      </p>
                    ) : null}
                  </div>
                ) : null}

                <button
                  type="submit"
                  style={{
                    ...styles.analyzeButton,
                    ...(starting ? styles.buttonDisabled : null),
                  }}
                  disabled={starting}
                >
                  <span style={styles.buttonSpark}>✦</span>
                  {starting
                    ? "Starting session…"
                    : isLoggedIn
                      ? "Start Vision Assist Session"
                      : "Log in to start"}
                </button>
              </FormStep>
            </section>
          </form>

          <div style={styles.guidanceStack} className="va-guidance">
            <GuidanceRow icon="☆" title="Best experience">
              Use a <strong>laptop/desktop</strong> with{" "}
              <strong>Chrome</strong> or <strong>Microsoft Edge</strong> for
              screen sharing. Mobile/tablet browsers may not allow screen
              sharing reliably.
            </GuidanceRow>
            <GuidanceRow icon="⇧" title="Uploads & analysis">
              Manual uploads support <strong>up to 30 pages per upload</strong>.
              Smaller uploads, like <strong>5–10 pages</strong>, usually produce
              the most precise guidance when you are focused on one section.
            </GuidanceRow>
            <GuidanceRow icon="◇" title="Privacy">
              Vision Assist processes your screen and form context securely. We
              do not save your login credentials, and we only use what we see
              to guide the current session.
            </GuidanceRow>
          </div>

          {onIOSApp ? (
            <p style={styles.iosNote}>
              Managing your billing? Please visit{" "}
              <strong>www.asklawly.com</strong> in your browser and sign in to
              your account.
            </p>
          ) : null}

          {sessionInfo ? (
            <div style={styles.successBox}>
              <strong>Session started.</strong>
              <div style={{ marginTop: 4 }}>
                Session ID:&nbsp;<code>{sessionInfo.session_id}</code>
              </div>
              <div>
                Worker region:&nbsp;<code>{sessionInfo.worker_region}</code>
              </div>
              {typeof sessionInfo.vision_assist_remaining_minutes ===
              "number" ? (
                <div>
                  Remaining minutes (snapshot):&nbsp;
                  <strong>
                    {sessionInfo.vision_assist_remaining_minutes} min
                  </strong>
                </div>
              ) : null}
              <div style={styles.jsonRow}>
                <pre style={{ margin: 0 }}>
                  {JSON.stringify(sessionInfo, null, 2)}
                </pre>
              </div>
            </div>
          ) : null}
        </main>

        <aside
          style={{
            ...styles.sideColumn,
            ...(isSmallScreen ? styles.sideColumnMobile : null),
          }}
        >
          <InfoPanel />
          <BillingPanel navigate={navigate} />
        </aside>
      </div>
    </div>
  );
}

const FeatureTile = ({ icon, title, text }) => (
  <div style={styles.featureTile}>
    <div style={styles.featureIcon}>{icon}</div>
    <div>
      <div style={styles.featureTitle}>{title}</div>
      <div style={styles.featureText}>{text}</div>
    </div>
  </div>
);

const FormStep = ({
  number,
  title,
  required = false,
  optional = false,
  children,
}) => (
  <section style={styles.stepSection}>
    <div className="vision-assist-step-header" style={styles.stepHeader}>
      <div style={styles.stepHeadingGroup}>
        <div style={styles.stepBadge}>{number}</div>
        <div style={styles.stepTitleBlock}>
          <div style={styles.stepTitleRow}>
            <h2 style={styles.h2}>{title}</h2>
            {required ? (
              <span style={styles.requiredPill}>Required</span>
            ) : null}
            {optional ? (
              <span style={styles.optionalPill}>Optional</span>
            ) : null}
          </div>
        </div>
      </div>
    </div>

    <div className="vision-assist-step-body" style={styles.stepBody}>
      {children}
    </div>
  </section>
);

const InfoPanel = () => (
  <section style={styles.sidePanel}>
    <div style={styles.sideHeaderRow}>
      <div style={styles.sideIcon}>i</div>
      <div style={styles.sideTitle}>How it works</div>
    </div>
    <div style={styles.desktopBadge}>▣ Best on laptop/desktop</div>
    <div style={styles.stepMiniList}>
      <MiniStep number="1" title="Describe your task" text="Tell us what you’re working on." />
      <MiniStep number="2" title="Add the site host" text="Optionally name the website or portal." />
      <MiniStep number="3" title="Start your session" text="We open a guided Vision Assist session and help step by step." last />
    </div>
  </section>
);

const BillingPanel = ({ navigate }) => (
  <section style={styles.sidePanel}>
    <div style={styles.sideHeaderRow}>
      <div style={styles.sideIcon}>▣</div>
      <div style={styles.sideTitle}>Billing & usage</div>
    </div>
    <p style={styles.sideCopy}>Vision Assist uses minutes from your plan.</p>
    <p style={styles.sideCopy}>If your plan is expired or you have no remaining minutes, you won’t be able to start a session.</p>
    <p style={styles.sideCopy}>Check your usage, plan details, and history on the Billing page.</p>
    <button type="button" style={styles.sideButton} onClick={() => navigate("/billing")}>
      Manage packages <span aria-hidden>›</span>
    </button>
  </section>
);

const MiniStep = ({ number, title, text, last = false }) => (
  <div style={styles.miniStep}>
    <div style={styles.miniStepLeft}>
      <div style={styles.miniStepNumber}>{number}</div>
      {!last && <div style={styles.miniStepLine} />}
    </div>
    <div style={styles.miniStepBody}>
      <div style={styles.miniStepTitle}>{title}</div>
      <div style={styles.miniStepText}>{text}</div>
    </div>
  </div>
);

const GuidanceRow = ({ icon, title, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={styles.guidanceRow}>
      <button
        type="button"
        style={styles.guidanceButton}
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        <span style={styles.guidanceIcon}>{icon}</span>
        <span style={styles.guidanceTitle}>{title}</span>
        <span style={styles.guidanceChevron}>{open ? "−" : "⌄"}</span>
      </button>
      {open && <div style={styles.guidanceBody}>{children}</div>}
    </div>
  );
};

const styles = {
  outerWrap: {
    width: "100%",
    maxWidth: 1120,
    minWidth: 0,
    margin: "0 auto",
    padding: "18px 16px 44px",
    background:
      "radial-gradient(ellipse 82% 68% at 58% 28%, rgba(37,99,235,0.08) 0%, rgba(37,99,235,0.04) 46%, rgba(37,99,235,0) 100%), radial-gradient(ellipse 72% 58% at 36% 72%, rgba(59,130,246,0.045) 0%, rgba(59,130,246,0.022) 48%, rgba(59,130,246,0) 100%), #FFFFFF",
    color: "#0F172A",
    fontFamily: "sans-serif",
    minHeight: "100vh",
    overflowX: "hidden",
    boxSizing: "border-box",
  },
  outerWrapMobile: {
    padding: "14px 12px 34px",
  },

  pageHeader: {
    marginBottom: 18,
    paddingTop: 12,
  },
  pageHeaderText: {
    minWidth: 0,
  },
  eyebrow: {
    color: "#2563EB",
    fontSize: 13,
    fontWeight: 950,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginBottom: 7,
  },
  eyebrowMobile: {
    textAlign: "center",
  },
  h1: {
    margin: 0,
    fontSize: 30,
    lineHeight: 1.12,
    fontWeight: 950,
    letterSpacing: "-0.035em",
  },
  h1Mobile: {
    fontSize: 28,
    lineHeight: 1.18,
    textAlign: "center",
  },
  subtitle: {
    margin: "8px 0 0",
    color: "#475569",
    fontSize: 15,
    lineHeight: 1.55,
    fontWeight: 650,
    maxWidth: 840,
  },
  subtitleMobile: {
    textAlign: "center",
    marginLeft: "auto",
    marginRight: "auto",
  },

  featureGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 14,
    marginBottom: 16,
  },
  featureGridMobile: {
    gridTemplateColumns: "1fr",
    gap: 10,
  },
  featureTile: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minHeight: 82,
    padding: 14,
    borderRadius: 16,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "#FFFFFF",
    boxShadow: "0 10px 26px rgba(2,6,23,0.055)",
  },
  featureIcon: {
    width: 44,
    height: 44,
    minWidth: 44,
    borderRadius: 14,
    background: "rgba(37,99,235,0.10)",
    color: "#2563EB",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 22,
    fontWeight: 950,
  },
  featureTitle: {
    margin: "0 0 5px",
    fontSize: 14.5,
    fontWeight: 950,
    lineHeight: 1.25,
  },
  featureText: {
    color: "#475569",
    fontSize: 13.5,
    lineHeight: 1.42,
    fontWeight: 650,
  },

  pageGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 290px",
    gap: 16,
    alignItems: "start",
  },
  pageGridMobile: {
    gridTemplateColumns: "1fr",
  },
  mainColumn: {
    minWidth: 0,
  },

  workflowPanel: {
    display: "grid",
    gap: 14,
    borderRadius: 18,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "#F8FAFC",
    boxShadow: "0 14px 34px rgba(2,6,23,0.07)",
    padding: 14,
    overflow: "visible",
  },
  stepSection: {
    minWidth: 0,
    border: "1px solid rgba(2,6,23,0.10)",
    borderRadius: 17,
    background: "#FFFFFF",
    padding: 16,
    boxShadow: "0 7px 20px rgba(2,6,23,0.035)",
  },
  stepHeader: {
    width: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  stepHeadingGroup: {
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  stepBadge: {
    width: 38,
    height: 38,
    flex: "0 0 38px",
    borderRadius: 999,
    background: "#2563EB",
    border: "1px solid #2563EB",
    color: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 15,
    fontWeight: 950,
    boxShadow: "0 6px 14px rgba(37,99,235,0.18)",
  },
  stepTitleBlock: {
    minWidth: 0,
  },
  stepTitleRow: {
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  h2: {
    minWidth: 0,
    margin: 0,
    fontSize: 20,
    lineHeight: 1.25,
    fontWeight: 950,
    letterSpacing: "-0.3px",
  },
  requiredPill: {
    flex: "0 0 auto",
    border: "1px solid #FECACA",
    borderRadius: 999,
    background: "#FEF2F2",
    color: "#B91C1C",
    padding: "4px 7px",
    fontSize: 9.5,
    lineHeight: 1,
    fontWeight: 950,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  optionalPill: {
    flex: "0 0 auto",
    border: "1px solid rgba(100,116,139,0.20)",
    borderRadius: 999,
    background: "rgba(100,116,139,0.08)",
    color: "#64748B",
    padding: "4px 7px",
    fontSize: 9.5,
    lineHeight: 1,
    fontWeight: 950,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  stepBody: {
    width: "100%",
    minWidth: 0,
    paddingTop: 16,
  },

  helperLine: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    marginTop: 9,
    color: "#64748B",
    fontSize: 13.25,
    lineHeight: 1.45,
    fontWeight: 700,
  },
  infoDot: {
    width: 18,
    height: 18,
    minWidth: 18,
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(30,42,120,0.08)",
    border: "1px solid rgba(30,42,120,0.14)",
    color: "#1E2A78",
    fontWeight: 950,
    fontSize: 11,
    lineHeight: 1,
  },

  input: {
    width: "100%",
    padding: "12px 14px",
    fontSize: 15,
    borderRadius: 12,
    border: "1px solid rgba(2,6,23,0.16)",
    background: "#FFFFFF",
    outline: "none",
    color: "#0F172A",
    boxShadow: "0 8px 18px rgba(2,6,23,0.04)",
    fontWeight: 700,
  },
  inputFocus: {
    border: "1px solid #2563EB",
    boxShadow: "0 0 0 4px rgba(37,99,235,0.16)",
  },
  textarea: {
    minHeight: 132,
    resize: "vertical",
    lineHeight: 1.5,
  },
  voiceShell: {
    width: "100%",
    position: "relative",
    borderRadius: 12,
    background: "#fff",
    overflow: "visible",
  },
  voiceTextarea: {
    minHeight: 180,
    resize: "vertical",
    lineHeight: 1.55,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    paddingRight: 64,
    paddingBottom: 58,
    display: "block",
  },
  voiceWave: {
    height: 180,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    padding: "16px 12px 58px",
    border: "1px solid #d1d5db",
    borderRadius: 12,
    background: "#fff",
  },
  voiceBars: {
    display: "flex",
    width: "100%",
    gap: 2,
    alignItems: "center",
  },
  voiceActions: {
    position: "absolute",
    right: 16,
    bottom: 14,
    zIndex: 3,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
  },
  voiceButton: {
    backgroundColor: "#f2f2f2",
    border: "none",
    borderRadius: 8,
    padding: 10,
    minWidth: 38,
    minHeight: 38,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#111827",
    boxShadow: "0 2px 8px rgba(15,23,42,0.08)",
  },
  voiceHint: {
    marginTop: 7,
    fontSize: 12,
    lineHeight: 1.4,
    color: "#6B7280",
  },
  goalMetaRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    marginTop: 9,
  },
  helperLineCompact: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    color: "#64748B",
    fontSize: 13.25,
    lineHeight: 1.45,
    fontWeight: 700,
    flex: "1 1 auto",
  },
  goalCounter: {
    flex: "0 0 auto",
    color: "#64748B",
    fontSize: 12,
    lineHeight: 1.45,
    fontWeight: 800,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  goalLimitNotice: {
    marginTop: 7,
    color: "#B91C1C",
    fontSize: 12,
    lineHeight: 1.4,
    fontWeight: 700,
  },
  inlineError: {
    marginTop: 8,
    fontSize: 12.8,
    color: "#B91C1C",
    fontWeight: 850,
    lineHeight: 1.42,
  },
  termsLabel: {
    display: "inline-flex",
    alignItems: "flex-start",
    gap: 9,
    fontSize: 13.5,
    color: "#334155",
    lineHeight: 1.5,
    marginTop: 16,
    marginBottom: 12,
    fontWeight: 750,
    textAlign: "left",
  },
  termsCheckbox: {
    marginTop: 3,
    width: 16,
    height: 16,
    accentColor: "#2563EB",
  },

  notice: {
    margin: "4px 0 12px",
    borderRadius: 13,
    padding: "10px 12px",
    fontSize: 13,
    lineHeight: 1.45,
    fontWeight: 750,
    border: "1px solid",
  },
  noticeOk: {
    background: "rgba(5,150,105,0.08)",
    color: "#065F46",
    borderColor: "rgba(5,150,105,0.18)",
  },
  noticeError: {
    background: "rgba(185,28,28,0.06)",
    color: "#991B1B",
    borderColor: "rgba(185,28,28,0.16)",
  },
  noticeBilling: {
    background: "rgba(245,158,11,0.08)",
    color: "#92400E",
    borderColor: "rgba(245,158,11,0.20)",
  },
  noticeText: {
    margin: 0,
  },
  noticeSubText: {
    margin: "5px 0 0",
    color: "#475569",
    fontSize: 13,
    lineHeight: 1.45,
  },

  analyzeButton: {
    appearance: "none",
    border: "1px solid rgba(37,99,235,0.22)",
    background: "#2563EB",
    color: "#FFFFFF",
    borderRadius: 12,
    padding: "13px 18px",
    fontSize: 14.5,
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "0 14px 26px rgba(37,99,235,0.22)",
    width: "100%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  buttonSpark: {
    fontWeight: 950,
  },
  buttonDisabled: {
    opacity: 0.58,
    cursor: "not-allowed",
    boxShadow: "none",
  },

  voiceBillingOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 10020,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 18,
    background: "rgba(15,23,42,0.42)",
    backdropFilter: "blur(5px)",
    WebkitBackdropFilter: "blur(5px)",
  },
  voiceBillingCard: {
    position: "relative",
    width: "min(460px, calc(100vw - 32px))",
    borderRadius: 20,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "#FFFFFF",
    boxShadow: "0 28px 70px rgba(15,23,42,0.22)",
    padding: "26px 24px 22px",
    textAlign: "center",
  },
  voiceBillingClose: {
    position: "absolute",
    top: 12,
    right: 12,
    width: 34,
    height: 34,
    borderRadius: 999,
    border: "none",
    background: "#F1F5F9",
    color: "#475569",
    fontSize: 22,
    lineHeight: 1,
    cursor: "pointer",
  },
  voiceBillingIcon: {
    width: 46,
    height: 46,
    margin: "0 auto",
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#EFF6FF",
    color: "#2563EB",
    fontSize: 19,
    fontWeight: 950,
  },
  voiceBillingEyebrow: {
    marginTop: 13,
    color: "#2563EB",
    fontSize: 12,
    fontWeight: 950,
    letterSpacing: "0.035em",
    textTransform: "uppercase",
  },
  voiceBillingTitle: {
    margin: "8px 0 0",
    color: "#0F172A",
    fontSize: 20,
    lineHeight: 1.3,
    fontWeight: 950,
    letterSpacing: "-0.02em",
  },
  voiceBillingText: {
    margin: "11px auto 0",
    maxWidth: 390,
    color: "#475569",
    fontSize: 13.5,
    lineHeight: 1.6,
    fontWeight: 700,
  },
  voiceBillingActions: {
    marginTop: 20,
    display: "flex",
    justifyContent: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  voiceBillingPrimaryButton: {
    minWidth: 130,
    border: "none",
    borderRadius: 12,
    background: "#2563EB",
    color: "#FFFFFF",
    padding: "10px 15px",
    fontWeight: 900,
    cursor: "pointer",
  },
  voiceBillingSecondaryButton: {
    minWidth: 100,
    border: "1px solid rgba(15,23,42,0.12)",
    borderRadius: 12,
    background: "#FFFFFF",
    color: "#334155",
    padding: "10px 15px",
    fontWeight: 850,
    cursor: "pointer",
  },

  voiceNoticeBackdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 100000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    background: "rgba(15, 23, 42, 0.38)",
  },
  voiceNoticeCard: {
    width: "min(440px, calc(100vw - 32px))",
    maxWidth: "100%",
    borderRadius: 14,
    background: "#FFFFFF",
    border: "1px solid #E2E8F0",
    boxShadow: "0 24px 64px rgba(15, 23, 42, 0.24)",
    overflow: "hidden",
  },
  voiceNoticeHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "16px 18px 10px",
  },
  voiceNoticeTitle: {
    margin: 0,
    color: "#0F172A",
    fontSize: 16,
    lineHeight: 1.3,
    fontWeight: 800,
  },
  voiceNoticeClose: {
    width: 34,
    height: 34,
    flex: "0 0 34px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: 0,
    borderRadius: 8,
    background: "transparent",
    color: "#64748B",
    cursor: "pointer",
  },
  voiceNoticeBody: {
    padding: "2px 18px 18px",
    color: "#334155",
    fontSize: 14,
    lineHeight: 1.55,
    whiteSpace: "pre-wrap",
  },
  voiceNoticeFooter: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "0 18px 18px",
  },
  voiceNoticeOkay: {
    minWidth: 84,
    minHeight: 38,
    padding: "8px 16px",
    border: 0,
    borderRadius: 9,
    background: "#1E2A78",
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
  },

  sideColumn: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
    minWidth: 0,
  },
  sideColumnMobile: {
    gridRow: 2,
  },
  sidePanel: {
    borderRadius: 16,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "#FFFFFF",
    boxShadow: "0 10px 26px rgba(2,6,23,0.055)",
    padding: 14,
  },
  sideHeaderRow: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    marginBottom: 12,
  },
  sideIcon: {
    width: 26,
    height: 26,
    minWidth: 26,
    borderRadius: 9,
    border: "1px solid rgba(30,42,120,0.16)",
    background: "rgba(30,42,120,0.06)",
    color: "#1E2A78",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 950,
    fontSize: 13,
  },
  sideTitle: {
    color: "#0F172A",
    fontSize: 15,
    fontWeight: 950,
  },
  desktopBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    color: "#2563EB",
    background: "rgba(37,99,235,0.08)",
    border: "1px solid rgba(37,99,235,0.18)",
    borderRadius: 10,
    padding: "8px 10px",
    fontSize: 12.5,
    fontWeight: 900,
    marginBottom: 12,
  },
  stepMiniList: {
    display: "flex",
    flexDirection: "column",
  },
  miniStep: {
    display: "grid",
    gridTemplateColumns: "30px minmax(0, 1fr)",
    gap: 9,
    minHeight: 72,
  },
  miniStepLeft: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
  },
  miniStepNumber: {
    width: 24,
    height: 24,
    borderRadius: 999,
    border: "1px solid rgba(37,99,235,0.45)",
    background: "#FFFFFF",
    color: "#2563EB",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 950,
    zIndex: 1,
  },
  miniStepLine: {
    position: "absolute",
    top: 26,
    bottom: 2,
    width: 1,
    background: "rgba(37,99,235,0.25)",
  },
  miniStepBody: {
    paddingBottom: 12,
  },
  miniStepTitle: {
    fontSize: 13.25,
    fontWeight: 950,
    marginBottom: 4,
  },
  miniStepText: {
    color: "#475569",
    fontSize: 12.5,
    lineHeight: 1.45,
    fontWeight: 650,
  },
  sideCopy: {
    margin: "0 0 10px",
    color: "#475569",
    fontSize: 13,
    lineHeight: 1.55,
    fontWeight: 700,
  },
  sideButton: {
    appearance: "none",
    border: "1px solid rgba(37,99,235,0.55)",
    background: "#FFFFFF",
    color: "#2563EB",
    borderRadius: 12,
    padding: "10px 12px",
    width: "100%",
    cursor: "pointer",
    fontWeight: 950,
    fontSize: 13.5,
  },

  guidanceStack: {
    marginTop: 14,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  guidanceRow: {
    borderRadius: 14,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "#FFFFFF",
    boxShadow: "0 8px 20px rgba(2,6,23,0.04)",
    overflow: "hidden",
  },
  guidanceButton: {
    appearance: "none",
    width: "100%",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: "12px 14px",
    display: "grid",
    gridTemplateColumns: "30px 1fr 24px",
    alignItems: "center",
    gap: 10,
    textAlign: "left",
  },
  guidanceIcon: {
    width: 26,
    height: 26,
    borderRadius: 10,
    background: "rgba(30,42,120,0.07)",
    color: "#1E2A78",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 950,
  },
  guidanceTitle: {
    fontSize: 14,
    fontWeight: 900,
  },
  guidanceChevron: {
    justifySelf: "end",
    color: "#334155",
    fontSize: 18,
    fontWeight: 950,
  },
  guidanceBody: {
    padding: "0 14px 14px 54px",
    color: "#475569",
    fontSize: 13.25,
    lineHeight: 1.55,
    fontWeight: 650,
  },

  iosNote: {
    margin: "14px 0 0",
    color: "#475569",
    fontSize: 13,
    lineHeight: 1.5,
    fontStyle: "italic",
    fontWeight: 700,
  },
  successBox: {
    marginTop: 14,
    borderRadius: 14,
    padding: "12px 14px",
    backgroundColor: "#ECFDF5",
    color: "#047857",
    border: "1px solid rgba(4,120,87,0.18)",
    fontSize: 12.5,
    lineHeight: 1.5,
    fontWeight: 750,
  },
  jsonRow: {
    marginTop: 8,
    fontFamily:
      "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
    fontSize: 11,
    backgroundColor: "#F8FAFC",
    borderRadius: 10,
    padding: 8,
    border: "1px solid rgba(2,6,23,0.08)",
    maxHeight: 160,
    overflow: "auto",
    color: "#0F172A",
  },

  deviceOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.48)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    zIndex: 9999,
  },
  deviceModal: {
    width: "min(560px, 100%)",
    background: "#FFFFFF",
    borderRadius: 18,
    border: "1px solid rgba(2,6,23,0.10)",
    boxShadow: "0 18px 48px rgba(2,6,23,0.22)",
    padding: 18,
  },
  deviceTitle: {
    fontSize: 18,
    fontWeight: 950,
    color: "#0F172A",
    textAlign: "center",
    marginBottom: 9,
    letterSpacing: "-0.25px",
  },
  deviceBody: {
    fontSize: 14,
    color: "#334155",
    lineHeight: 1.55,
    textAlign: "center",
    marginBottom: 14,
    fontWeight: 650,
  },
  deviceActions: {
    display: "flex",
    justifyContent: "center",
    marginTop: 8,
  },
  deviceBtn: {
    padding: "11px 18px",
    borderRadius: 12,
    border: "1px solid rgba(37,99,235,0.22)",
    background: "#2563EB",
    color: "#FFFFFF",
    cursor: "pointer",
    fontWeight: 950,
    fontSize: 13.5,
    minWidth: 160,
    boxShadow: "0 12px 24px rgba(37,99,235,0.20)",
  },
  deviceFootnote: {
    marginTop: 11,
    fontSize: 12.5,
    color: "#64748B",
    textAlign: "center",
    lineHeight: 1.45,
    fontWeight: 650,
  },
};
